import React, { useCallback } from 'react';

import Tag from 'antd/es/tag';
import Typography from 'antd/es/typography';
import Modal from 'antd/es/modal';

import colors from 'config/colors';
import useModal from 'hooks/useModal';

import { ResourceType } from 'services/resources';
import useAdminResource from '../hooks/useAdminResource';

import AdminLayout from 'layouts/Admin.layout';
import { Row, Col, Space } from 'components/layout';
import { Title } from 'components/Text';
import FormModal from 'components/FormModal';
import Table from 'components/Table';
import Button from 'components/Button';
import Search from 'components/Search';

import { EditAction, DeleteAction } from '../components/TableActions';
import CameraForm from '../components/forms/Camera.form';
import CameraInfo from '../components/CameraInfo';

import CameraSvg from '../assets/images/camera.svg';
import useResource from 'hooks/useResource';
import { mapDoorsToOptions } from 'helpers/mapToOptions';

const CamerasPage = () => {
  const infoModal = useModal();
  const doors = useResource(ResourceType.doors, { prefetch: true });

  const cameras = useAdminResource({
    resource: ResourceType.cameras,
    resourceName: 'camera',
    searchFields: [
      'name',
      'brand',
      'user',
    ],
  });

  const onAction = useCallback((actionType, item) => {
    switch (actionType) {
      case EditAction.actionType:
        cameras.onEdit(item);
        break
      case DeleteAction.actionType:
        cameras.onDelete(item.camera_id);
        break;
      default: // make eslint happy :)
    }
  }, [cameras]);

  return (
    <AdminLayout
      title="Cameras"
      icon="video"
      extra={[
        <Search
          placeholder={cameras.strings.searchPlaceholder}
          value={cameras.searchVal}
          onChange={value => cameras.search(value)}
        />,
        <Button
          type="primary"
          icon="plus"
          onClick={() => {
            cameras.onCreate({ is_active: 1 });
          }}
        >
          {cameras.strings.addButton}
        </Button>
      ]}
    >
      <FormModal
        title={cameras.strings.formTitle}
        form={cameras.form}
        submitText={cameras.strings.formSubmitButton}
        onClose={() => cameras.form.reset()}
        disabledButtons={cameras.form.isSubmitting}
      >
        <CameraForm form={cameras.form} doorOptions={mapDoorsToOptions(doors.state.list)} />
      </FormModal>
      <Table
        loading={cameras.isLoading}
        dataSource={cameras.list}
        actions={[
          EditAction,
          DeleteAction,
        ]}
        actionsProps={{
          resource: 'camera',
        }}
        onAction={onAction}
        rowKey="camera_id"
      >
        <Table.Column title="ID" dataIndex="camera_id" />
        <Table.Column title="Name" dataIndex="name"
          render={cameraName => (
            <Typography.Link as="span" style={{ color: colors.primary }}>
              {cameraName}
            </Typography.Link>
          )}
          onCell={item => {
            return {
              onClick: () => {
                infoModal.show(item);
              }
            };
          }
          } />
        <Table.Column title="IP" dataIndex="ip" />
        <Table.Column title="Brand" dataIndex="brand" />
        <Table.Column title="Floor Height" dataIndex="floor_height" />
        <Table.Column title="User" dataIndex="user" />
        <Table.Column title="Pass" dataIndex="password" />
        <Table.Column title="Status" dataIndex="is_active" render={isActive => {
          return isActive === true || isActive === 1 ? (
            <Tag color='green'>Running</Tag>
          ) : (
            <Tag color='#828C94'>Not Responding</Tag>
          );
        }} />
      </Table>

      <Modal
        title={'Camera'}
        visible={infoModal.isVisible}
        onCancel={infoModal.hide}
        footer={
          <>
            <Button
              icon="edit"
              onClick={() => {
                infoModal.hide();
                cameras.onEdit(infoModal.data);
              }}
            >
              Edit
            </Button>
            <Button
              onClick={() => {
                infoModal.hide();
              }}
            >
              Close
            </Button>
          </>
        }
      >
        <Row style={{ padding: '0 24px' }}>
          <Col span={24}>
            <Space size="large" style={{ marginBottom: '24px' }}>
              <img alt="camera vector" src={CameraSvg} />
              <Title level={4}>
                {infoModal.data && infoModal.data.name}
              </Title>
            </Space>
            <CameraInfo camera={infoModal.data} />
          </Col>
        </Row>
      </Modal>
    </AdminLayout>
  );
};
export default CamerasPage;